/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './styles.module.scss';
import { Actionable, Icon, StarFill, Text, View } from '@az/starc-ui';
import getPosition from '@/utils/crossBrowserHelpers/getPosition';
import scrollToElem from '@/utils/crossBrowserHelpers/scroll';
import { useReviewFilters } from '@/features/customReviews/context/ReviewFiltersProvider';
import { useBvPixelTracking } from '../../../hooks/useBvPixelTracking';

type RatingDistributionProps = {
  stars: string;
  average: string;
  noReviews?: boolean;
  close?: VoidFunction;
};

export const Distribution = ({
  stars,
  average,
  noReviews = false,
  close,
}: RatingDistributionProps) => {
  const { setSelectedRatings } = useReviewFilters();
  const { trackEvent } = useBvPixelTracking();
  const scrollToCustomerReviews = () => {
    const title = document.getElementById('reviews-list');

    if (title != null) {
      const client = getPosition(title);

      if (client.y !== 0) {
        scrollToElem(client.y - 105);
      }
    }
  };

  return (
    <Actionable
      onClick={() => {
        scrollToCustomerReviews();
        setSelectedRatings([parseInt(stars)]);
        trackEvent('Rating Distribution', 'Click', 'Filter', stars);
        close?.();
      }}
      disabled={noReviews}
      attributes={{
        'data-testid': 'distribution',
        'data-stars': stars,
        'data-average': average,
        'data-noreviews': noReviews,
      }}
    >
      <View direction={'row'} align={'center'} className={styles.distributionContainer}>
        <Text
          variant="inline-link"
          decoration="underline"
          weight="medium"
          className={styles.starsButton}
        >
          {stars === '1' ? `${stars} star` : `${stars} stars`}
        </Text>
        <Icon svg={StarFill} color={noReviews ? '300' : 'accent'} size={{ s: 6, m: 7 }} />
        <div className={styles.barContainer}>
          <span className={styles.bar} style={{ width: `${average}%` }}></span>
        </div>
        <Text
          variant="inline-link"
          decoration="underline"
          weight="medium"
          className={styles.avgButton}
          align="center"
        >
          {noReviews ? '0%' : `${average}%`}
        </Text>
      </View>
    </Actionable>
  );
};
